import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PinInput from 'src/components/signature/pin-input';
import SetupSigningWizard from 'src/components/signature/setup-signing-wizard';
import SignatureRender from 'src/components/signature/signature-render';
import SigningLockedDialog from 'src/components/signature/signing-locked-dialog';
import { useSignatureValidation } from 'src/hooks/use-signature-validation';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import { newDocumentSignAction } from 'src/redux/actions/documentationAction';

interface SignatureModalProps {
  open: boolean;
  onClose: () => void;
  setIsDocSigned: any;
  formSubmissionUuid: string;
  isSupervisor: boolean;
  docType: any;
}

const SignVector = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M12.9264 3.05139L14.3326 1.64515C14.9428 1.03495 15.9322 1.03495 16.5424 1.64515C17.1526 2.25534 17.1526 3.24466 16.5424 3.85485L7.69349 12.7037C7.25292 13.1443 6.70953 13.4681 6.11241 13.646L3.875 14.3125L4.54148 12.0751C4.71935 11.478 5.04321 10.9346 5.48377 10.494L12.9264 3.05139ZM12.9264 3.05139L15.125 5.24999M13.875 10.9792V14.9375C13.875 15.973 13.0355 16.8125 12 16.8125H3.25C2.21447 16.8125 1.375 15.973 1.375 14.9375V6.18749C1.375 5.15196 2.21447 4.31249 3.25 4.31249H7.20833"
        stroke="white"
        strokeWidth="1.3125"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const SignDocementModel: FC<SignatureModalProps> = ({
  open,
  onClose,
  formSubmissionUuid,
  isSupervisor,
  setIsDocSigned,
  docType,
}) => {
  const dispatch = useDispatch();

  const therapistProfile = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data
  );

  //  THIN-7681: when the tenant flag is on, the signer's name is HR-rendered (view-only)
  //  and a signing PIN is required in place of the free-typed name.
  const { enabled: signatureValidationEnabled, hasPin, statusLoading } = useSignatureValidation();
  const fullName = [therapistProfile?.lastName, therapistProfile?.firstName]
    .filter(Boolean)
    .join(' ');
  //  First sign without a PIN → run the one-time setup wizard before the challenge.
  const showSetup = signatureValidationEnabled && !hasPin && !statusLoading;

  const newDocumentSignReducer = useSelector((state: any) => state.newDocumentSignReducer);
  const [signatureName, setSignatureName] = useState('');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [locked, setLocked] = useState<{ open: boolean; until: string | null }>({
    open: false,
    until: null,
  });
  const [agree, setAgree] = useState(false);
  const [popUpMessage, setPopUpMessage] = useState({ type: '', message: '' });
  //  Lets us hide the dialog instantly on success while the component (and its snackbar)
  //  stays mounted so the existing success snackbar still shows before we fully close.
  const [dialogVisible, setDialogVisible] = useState(true);

  useEffect(() => {
    if (open) {
      setPin('');
      setPinError(null);
      setDialogVisible(true);
    }
  }, [open]);

  useEffect(() => {
    if (newDocumentSignReducer?.response === null && !newDocumentSignReducer?.isSuccess) return;

    if (newDocumentSignReducer?.response !== null && !newDocumentSignReducer?.isSuccess) {
      //  ERROR OCCURRED. When the flag is on, a PIN challenge failure carries a structured
      //  outcome (THIN-7706) that we surface inline / as a lockout rather than a generic toast.
      const challenge = signatureValidationEnabled ? newDocumentSignReducer?.response?.data : null;
      const outcome = challenge?.outcome;

      if (outcome === 'LOCKED') {
        setLocked({ open: true, until: challenge?.lockedUntil ?? null });
        setPin('');
        return;
      }
      if (outcome === 'WRONG_PIN') {
        const left = challenge?.attemptsRemaining;
        setPinError(
          `Incorrect PIN.${
            left != null ? ` ${left} attempt${left === 1 ? '' : 's'} remaining.` : ''
          }`
        );
        setPin('');
        return;
      }
      if (outcome === 'NO_PIN_SET') {
        setPinError('No signing PIN set yet. Set one up in Profile → Signature & PIN.');
        return;
      }

      setPopUpMessage({ message: newDocumentSignReducer?.response?.message, type: 'ERROR' });
      setTimeout(() => {
        setPopUpMessage({ message: '', type: '' });
        onClose();
      }, 3000);
      return;
    }

    //  SUCCESS — signature signed successfully.
    setIsDocSigned(newDocumentSignReducer?.response?.data);
    setPopUpMessage({ message: newDocumentSignReducer?.response?.message, type: '' });
    if (signatureValidationEnabled) {
      //  THIN-7681 UX: hide the dialog immediately (so the now-empty PIN form doesn't linger)
      //  but keep the component mounted so the existing SimpleSnackbar still pops; fully close
      //  once it's been shown.
      setPin('');
      setDialogVisible(false);
      setTimeout(() => {
        setPopUpMessage({ message: '', type: '' });
        onClose();
      }, 3000);
      return;
    }
    setTimeout(() => {
      setPopUpMessage({ message: '', type: '' });
      onClose();
    }, 3000);
  }, [newDocumentSignReducer]);

  const handleSign = () => {
    if (signatureValidationEnabled) {
      if (pin.length !== 4 || !agree) return;
      setPinError(null);
      dispatch(
        newDocumentSignAction({
          formSubmissionUuid,
          signerName: fullName, //  view-only; backend HR-renders the signed name (THIN-7695)
          consentConfirmation: agree,
          isSupervisor,
          documentType: docType?.replaceAll(' ', '_')?.toUpperCase(),
          pin,
        })
      );
      return;
    }

    //  Legacy free-text path (flag off) — unchanged.
    if (!signatureName.trim()) return;
    dispatch(
      newDocumentSignAction({
        formSubmissionUuid,
        signerName: signatureName,
        consentConfirmation: agree,
        isSupervisor,
        documentType: docType?.replaceAll(' ', '_')?.toUpperCase(),
      })
    );
    setSignatureName('');
  };

  const handleClose = () => {
    setSignatureName('');
    setPin('');
    setPinError(null);
    onClose();
  };

  const handleChangesStatus = (e: any) => {
    setAgree(e?.target?.checked);
  };

  const signDisabled = signatureValidationEnabled
    ? pin.length !== 4 || !agree
    : !signatureName.trim() || !agree;

  return (
    <>
      <Dialog
        open={open && dialogVisible}
        onClose={handleClose}
        maxWidth="sm"
      >
        {!showSetup && (
          <DialogTitle>{signatureValidationEnabled ? 'Sign Document' : 'Add Signature'}</DialogTitle>
        )}
        <DialogContent>
          {showSetup ? (
            <SetupSigningWizard onCancel={handleClose} />
          ) : (
            <>
          <Box sx={{ mb: '20px' }}>
            <Stack
              display={'flex'}
              flexDirection={'row'}
              gap={1}
            >
              <Typography variant="subtitle1">Name:</Typography>
              <Typography variant="body2">
                {therapistProfile?.lastName + ' ' + therapistProfile?.firstName}
              </Typography>
            </Stack>
            <Stack
              display={'flex'}
              flexDirection={'row'}
              gap={1}
            >
              <Typography variant="subtitle1">Credentials:</Typography>
              <Typography variant="body2">{therapistProfile?.credentials}</Typography>
            </Stack>
            <Stack
              display={'flex'}
              flexDirection={'row'}
              gap={1}
            >
              <Typography variant="subtitle1">License #:</Typography>
              <Typography variant="body2">{therapistProfile?.licenseNumber}</Typography>
            </Stack>
          </Box>

          {signatureValidationEnabled ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Your signature
              </Typography>
              {/* APIT-655: the preview has to show what the PDF will print. */}
              <SignatureRender
                name={fullName}
                license={therapistProfile?.licenseNumber}
                credentials={therapistProfile?.credentials}
                signatureLineOverride={therapistProfile?.signatureLineOverride}
                credentialsLineOverride={therapistProfile?.credentialsLineOverride}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Enter your 4-digit signing PIN
                </Typography>
                <PinInput value={pin} onChange={setPin} error={!!pinError} mask autoFocus />
                {pinError && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {pinError}
                  </Typography>
                )}
              </Box>
            </Box>
          ) : (
            <TextField
              fullWidth
              label="Type your signature"
              placeholder="Type your signature"
              value={signatureName}
              type="text"
              onChange={(e) => setSignatureName(e.target.value)}
              sx={{
                mb: 2,
                '& input': {
                  fontSize: 'larger',
                  fontStyle: 'oblique',
                },
              }}
            />
          )}

          <Stack
            display={'flex'}
            flexDirection={'row'}
            alignItems={'flex-start'}
            width={'420px'}
          >
            <Checkbox
              checked={agree}
              onChange={handleChangesStatus}
            />
            <Typography
              variant="body2"
              flexWrap={'wrap'}
            >
              I agree that my electronic signature is legally binding and holds the same validity as
              my handwritten signature, in accordance with applicable laws and regulations.
            </Typography>
          </Stack>
            </>
          )}
        </DialogContent>
        {!showSetup && (
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSign}
            disabled={signDisabled}
            startIcon={
              newDocumentSignReducer?.loading ? <CircularProgress size={20} /> : <SignVector />
            }
          >
            {newDocumentSignReducer?.loading ? 'Signing...' : 'Sign'}
          </Button>
        </DialogActions>
        )}
      </Dialog>

      {popUpMessage.message && (
        <SimpleSnackbar
          message={popUpMessage.message}
          isError={popUpMessage.type === 'ERROR'}
          isWarning={popUpMessage.type === 'WARNING'}
        />
      )}

      <SigningLockedDialog
        open={locked.open}
        lockedUntil={locked.until}
        onClose={() => setLocked({ open: false, until: null })}
      />
    </>
  );
};

export default SignDocementModel;
